<template>
    <BlockUI :blocked="loading">
        <Fluid>
            <h2>GitLab Integration</h2>
            <p class="description">
                Connect your GitLab account to enhance link previews and view
                merge request details directly within MyStekz.
            </p>
            <div class="settings-container">
                <div class="form-section">
                    <div class="field">
                        <label for="gitlab-url"
                            >GitLab URL <span class="required">*</span></label
                        >
                        <InputText
                            id="gitlab-url"
                            v-model="gitlabUrl"
                            placeholder="https://gitlab.com"
                            class="w-full"
                            :class="{ 'p-invalid': submitted && !gitlabUrl }"
                        />
                        <small class="help-text">
                            Enter GitLab.com for GitLab.com, or your self-hosted
                            GitLab URL
                        </small>
                        <small v-if="submitted && !gitlabUrl" class="p-error">
                            GitLab URL is required
                        </small>
                    </div>
                    <div class="field">
                        <label for="access-token"
                            >Personal/Project Access Token
                            <span class="required">*</span></label
                        >
                        <Password
                            id="access-token"
                            v-model="accessToken"
                            placeholder="glpat-xxxxxxxxxxxx"
                            :feedback="false"
                            toggleMask
                            class="w-full"
                            :class="{ 'p-invalid': submitted && !accessToken }"
                            inputClass="w-full"
                        />
                        <small class="help-text">
                            Enter a GitLab Access Token with 'read_api' scope
                        </small>
                        <small v-if="submitted && !accessToken" class="p-error">
                            Access token is required
                        </small>
                    </div>

                    <div class="field mt-4">
                        <Button
                            label="Test Connection"
                            icon="pi pi-check"
                            @click="testConnection"
                            :disabled="loading || !accessToken || !gitlabUrl"
                        />
                    </div>
                </div>

                <div class="info-section">
                    <h3>How to generate an access token:</h3>
                    <ol>
                        <li>
                            Go to your GitLab instance (GitLab.com or
                            self-hosted GitLab)
                        </li>
                        <li>
                            From your profile menu, select "Preferences" then
                            "Access Tokens"
                        </li>
                        <li>
                            Create a token with at least the "read_api" scope
                        </li>
                        <li>Copy the generated token and paste it here</li>
                    </ol>
                    <p class="note">
                        <i class="pi pi-info-circle"></i>
                        For project-specific access, you can also use Project
                        Access Tokens.
                    </p>
                </div>
            </div>
        </Fluid>

        <div v-if="error" class="error-message">
            <i class="pi pi-exclamation-triangle"></i>
            {{ error }}
        </div>

        <div v-if="connectionSuccess" class="success-message">
            <i class="pi pi-check-circle"></i>
            Connection successful! GitLab integration is working properly.
        </div>

        <div class="buttons">
            <Button
                v-if="isSetup"
                label="Delete Integration"
                icon="pi pi-trash"
                severity="danger"
                @click="confirmDelete"
                :disabled="loading"
                text
            />
            <div class="action-buttons">
                <Button
                    label="Close"
                    text
                    severity="secondary"
                    @click="close"
                    :disabled="loading"
                />
                <Button
                    label="Save"
                    icon="pi pi-check"
                    @click="saveSettings"
                    :disabled="loading"
                />
            </div>
        </div>
    </BlockUI>
</template>

<script setup lang="ts">
import {
    IntegrationType,
    useIntegrationManager,
    useIntegrationStore,
} from "@/stores/integration";
import BlockUI from "primevue/blockui";
import Button from "primevue/button";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Fluid from "primevue/fluid";
import InputText from "primevue/inputtext";
import Password from "primevue/password";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { inject, onMounted, Ref, ref } from "vue";

// Add type definitions for GitLab settings
interface GitLabSettings {
    host?: string;
    api_key?: string;
}

const toast = useToast();
const confirm = useConfirm();
const {
    fetchSettings,
    deleteSettings,
    createApiIntegration,
    testApiIntegration,
} = useIntegrationStore(IntegrationType.GITLAB);
const {
    integration,
    isSetup,
    error: integrationError,
} = useIntegrationManager(IntegrationType.GITLAB);
const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");

const loading = ref(false);
const error = ref("");
const submitted = ref(false);
const connectionSuccess = ref(false);
const defaultGitlabUrl = "https://gitlab.com";
const gitlabUrl = ref(defaultGitlabUrl);
const accessToken = ref("");

const loadSettings = async () => {
    try {
        loading.value = true;
        error.value = "";
        await fetchSettings();

        if (
            integration.value &&
            integration.value.integration_method === "api_key"
        ) {
            // Use typed settings
            const settings =
                (integration.value?.settings as GitLabSettings) || {};
            accessToken.value = settings.api_key || "";
            gitlabUrl.value = settings.host || defaultGitlabUrl;
        }
    } catch (err) {
        error.value = "Failed to load GitLab settings";
        console.error("Error loading GitLab settings:", err);
    } finally {
        loading.value = false;
    }
};

const testConnection = async () => {
    if (!accessToken.value || !gitlabUrl.value) {
        error.value = "Please provide both GitLab URL and access token";
        return;
    }

    try {
        loading.value = true;
        error.value = "";
        connectionSuccess.value = false;

        const validated = await testApiIntegration(
            accessToken.value.trim(),
            gitlabUrl.value.trim()
        );

        if (validated) {
            connectionSuccess.value = true;
            toast.add({
                severity: "success",
                summary: "Connection Successful",
                detail: "Successfully connected to GitLab API",
                life: 3000,
            });
        } else {
            error.value = integrationError.value || "Connection failed";
        }
    } catch (err) {
        const errorMessage =
            err instanceof Error ? err.message : "Unknown error";
        error.value =
            errorMessage ||
            "Failed to connect to GitLab. Please check your settings.";
    } finally {
        loading.value = false;
    }
};

const saveSettings = async () => {
    submitted.value = true;

    if (!accessToken.value || !gitlabUrl.value) {
        return;
    }

    try {
        loading.value = true;
        error.value = "";

        await createApiIntegration(accessToken.value, gitlabUrl.value);

        toast.add({
            severity: "success",
            summary: "Success",
            detail: "GitLab integration saved successfully",
            life: 3000,
        });

        close();
    } catch (err) {
        console.error("Error saving GitLab settings:", err);
        error.value = "Failed to save settings. Please try again.";
    } finally {
        loading.value = false;
    }
};

const confirmDelete = () => {
    confirm.require({
        message:
            "Are you sure you want to delete this GitLab integration? This action cannot be undone.",
        header: "Confirm Deletion",
        icon: "pi pi-exclamation-triangle",
        acceptProps: {
            severity: "danger",
        },
        accept: async () => {
            try {
                loading.value = true;
                error.value = "";

                await deleteSettings();

                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: "GitLab integration removed successfully",
                    life: 3000,
                });

                resetForm();
            } catch (err) {
                console.error("Error deleting GitLab settings:", err);
                error.value = "Failed to remove integration. Please try again.";
            } finally {
                loading.value = false;
            }
        },
    });
};

const resetForm = () => {
    gitlabUrl.value = defaultGitlabUrl;
    accessToken.value = "";
    submitted.value = false;
    error.value = "";
    connectionSuccess.value = false;
};

const close = () => {
    if (dialogRef?.value) {
        dialogRef.value.close();
    }
};

onMounted(() => {
    loadSettings();
});
</script>

<style lang="scss" scoped>
h2 {
    margin-top: 0;
    margin-bottom: 0.5rem;
}

h3 {
    margin-top: 0;
    font-size: 1rem;
    color: #495057;
}

.description {
    color: #6c757d;
    margin-bottom: 1.5rem;
}

.field {
    padding-bottom: 1em;

    label {
        display: block;
        margin-bottom: 0.5em;
        font-weight: 500;
    }
}

.settings-container {
    display: flex;
    gap: 2rem;

    .form-section {
        flex: 1;
    }

    .info-section {
        flex: 1;
        background-color: #f8f9fa;
        padding: 1rem;
        border-radius: 8px;

        ol {
            padding-left: 1.5rem;
            margin-top: 0.5rem;
            color: #495057;

            li {
                margin-bottom: 0.5rem;
            }
        }

        .note {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #e9ecef;
            color: #6c757d;
            font-size: 0.9rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
    }
}

.buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 1.5rem;
    align-items: center;
    border-top: 1px solid #f0f0f0;
    padding-top: 1rem;
}

.action-buttons {
    display: flex;
    gap: 1em;
}

.help-text {
    color: #6c757d;
    display: block;
    margin-top: 5px;
    font-size: 0.85rem;
}

.required {
    color: #f44336;
}

.error-message {
    background-color: #fff3f3;
    color: #f44336;
    padding: 10px;
    border-radius: 4px;
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.success-message {
    background-color: #f0f9e8;
    color: #4caf50;
    padding: 10px;
    border-radius: 4px;
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.w-full {
    width: 100%;
}

.mt-4 {
    margin-top: 1.5rem;
}

/* Responsive layout for smaller screens */
@media (max-width: 768px) {
    .settings-container {
        flex-direction: column;
    }
}
</style>
